import { Ref, forwardRef, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { LeafletEvent, Map, Icon, LatLngBounds, LatLngTuple, Point } from 'leaflet'
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet'

import { SisetuMapLayerType } from './types'
import { SisetuMarker } from './components/sisetuMarker'
import { ScoreMarker } from './components/scoreMarker'
import { LocationMarker } from './components/locationMarker'
import { useSisetuMapOverlay } from './container/useSisetuMapOverlay'

import { SisetuResult } from 'interface/sisetu'
import { PrefectureResult } from 'interface/prefecture'
import { mergeBoundary } from 'shared/location'

export * from './types'

Icon.Default.imagePath = '//cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/'

type MapEventProps = {
  onZoomEnd: (event: LeafletEvent) => void
  onChangeMapView: (map: Map, event: LeafletEvent) => void
}
const MapEvent = ({
  onZoomEnd,
  onChangeMapView
}: MapEventProps) => {
  const map = useMapEvents({
    zoomend: (event) => {
      console.log('map: zoomend', event)
      onZoomEnd(event)
      onChangeMapView(map, event)
    },
    resize: (event) => {
      console.log('map: resize', event)
      onChangeMapView(map, event)
    },
    moveend: (event) => {
      console.log('map: moveend', event)
      onChangeMapView(map, event)
    },
    load: (event) => {
      console.log('map: load', event)
      onChangeMapView(map, event)
    }
  })
  return <></>
}

export const useSisetuMap = () => {
  const mapRef = useRef<Map | null>(null)
  const invalidMapFlg = useRef<boolean>(false)
  const [showMapLayer, setShowMapLayer] = useState<SisetuMapLayerType>('pin')
  const [mapZooming, setMapZooming] = useState<boolean>(false)

  if (mapRef.current && invalidMapFlg.current === false) {
    invalidMapFlg.current = true
    // 初期処理
    const map = mapRef.current
    map.fireEvent('load')
    map.invalidateSize()
  }
	const panTo = useCallback(
		(sisetu: SisetuResult, animate: boolean) => {
			const location = sisetu.location
			if (!location || !location.latitude || !location.longitude) return
      
      if (mapRef.current) {
				setShowMapLayer('pin')
				try {
					if (animate) {
						setMapZooming(true)
						mapRef.current.flyTo([location.latitude, location.longitude], 15, { duration: 2 })
					} else {
						mapRef.current.panTo([location.latitude, location.longitude], { animate: false })
					}
				} catch (e) {
					console.error(e)
				}
			}
		},
		[mapRef, setShowMapLayer, setMapZooming]
	)

	const fitMap = useCallback(
		(prefectures: PrefectureResult[]) => {
      if (prefectures.length === 0) return

      const map = mapRef.current
      if (map) {
        const boundary = mergeBoundary(prefectures.map(prefecture => prefecture.boundary))
        map.fitBounds([
          [boundary.s, boundary.w],
          [boundary.n, boundary.e],
        ])
      }
		},
		[mapRef]
	)
	return useMemo(
		() => ({
			mapRef,
			invalidMapFlg,
			showMapLayer, setShowMapLayer,
			mapZooming, setMapZooming,
			panTo,
			fitMap
		}),
		[
			mapRef,
			invalidMapFlg,
			showMapLayer, setShowMapLayer,
			mapZooming, setMapZooming,
			panTo,
			fitMap
		]
	)
}

type SisetuMapViewProps = {
  sisetuList: SisetuResult[]
  selectedSisetuCode: string | null
  zooming: boolean
  onZoomEnd: (event: LeafletEvent) => void
  initialPosition?: LatLngTuple
  initialZoom?: number
  showLayer: SisetuMapLayerType
  onSisetuOpen: (sisetuCode: string) => void
}
const SisetuMapViewCore = ({
  sisetuList,
  selectedSisetuCode,
  zooming,
  onZoomEnd,
  initialPosition,
  initialZoom,
  showLayer,
  onSisetuOpen,
}: SisetuMapViewProps, ref: Ref<Map>) => {
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoom || 5)
  const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(null)
  const [mapSize, setMapSize] = useState<Point | null>(null)
  const [sisetuPopupOpen, setSisetuPopupOpen] = useState<boolean>(selectedSisetuCode ? true : false)

  const overlay = useSisetuMapOverlay({
    sisetuList,
    zoomLevel,
    mapBounds,
    mapSize,
    selectedSisetuCode,
    showLayer
  })

  const onChangeMapView = useCallback(
    (map: Map) => {
      setZoomLevel(map.getZoom())
      setMapBounds(map.getBounds())
      setMapSize(map.getSize())
    },
    [setZoomLevel, setMapBounds, setMapSize]
  )

  useEffect(
    () => {
      if (selectedSisetuCode) {
        setSisetuPopupOpen(true)
      }
    },
    [selectedSisetuCode]
  )

  return (
    <MapContainer
      ref={ref}
      center={initialPosition || [35.686991, 139.539242]}
      zoom={zoomLevel}
      scrollWheelZoom={true}
    >
      <MapEvent
        onZoomEnd={onZoomEnd}
        onChangeMapView={onChangeMapView}
      />

      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {zooming ? <></> : showLayer === 'pin' ? (
        <div>
          {overlay?.points.map(
            (point, i) => point.isCurrent ? <></> : (
              <LocationMarker
                key={`location-${i}`}
                point={point}
                onOpen={onSisetuOpen}
                onClose={() => setSisetuPopupOpen(false)}
              />
            )
          )}
        </div>
      ) : (
        <div>
          {overlay?.points.map((point, i) => (
            <ScoreMarker
              key={`dpc-${i}`}
              point={point}
              score={point.score}
              pathOptions={overlay.pathOptions}
              stroke={false}
              fillOpacity={0.5}
              onOpen={onSisetuOpen}
              onClose={() => setSisetuPopupOpen(false)}
            />
          ))}
        </div>
      )}
      {overlay?.current && (
        <SisetuMarker
          key="current-point"
          sisetu={overlay.current}
          active={true}
          openPopup={sisetuPopupOpen}
          onOpen={onSisetuOpen}
          onClose={() => setSisetuPopupOpen(false)}
        />
      )}
    </MapContainer>
  )
}
export const SisetuMapView = forwardRef<Map, SisetuMapViewProps>(SisetuMapViewCore)
